/**
 * Chunk U2 — OCR concurrency pool for multi-document upload.
 *
 * Schedules at most UPLOAD_QUEUE_OCR_CONCURRENCY jobs. Failures are isolated.
 * Cancel stops scheduling new work; in-flight jobs finish (results applied unless
 * discardInFlightResults is set); remaining QUEUED drafts stay for Review later.
 */

import {
  UPLOAD_DRAFT_STATUS,
  UPLOAD_QUEUE_OCR_CONCURRENCY,
  formatUploadQueueProgressLine,
  selectNextOcrDrafts,
  transitionUploadDraft,
} from './invoiceUploadQueueHelpers'

function replaceDraft(drafts, nextDraft) {
  const id = String(nextDraft?.id)
  return (drafts || []).map(d => (String(d?.id) === id ? nextDraft : d))
}

/**
 * @param {{
 *   initialDrafts: object[],
 *   concurrency?: number,
 *   processItem: (draft: object, ctx: { isCancelled: () => boolean }) => Promise<{
 *     ok: boolean,
 *     error?: string,
 *     patch?: object,
 *   }>,
 *   onDraftsChange?: (drafts: object[], meta: { progressLine: string, peakConcurrency: number }) => void,
 *   isCancelled?: () => boolean,
 * }} options
 */
export async function runOcrPool({
  initialDrafts = [],
  concurrency = UPLOAD_QUEUE_OCR_CONCURRENCY,
  processItem,
  onDraftsChange,
  isCancelled = () => false,
} = {}) {
  if (typeof processItem !== 'function') {
    throw new Error('runOcrPool requires processItem')
  }

  let drafts = [...(initialDrafts || [])]
  let peakConcurrency = 0
  let active = 0
  const inFlight = new Set()

  const emit = () => {
    if (typeof onDraftsChange === 'function') {
      onDraftsChange(drafts, {
        progressLine: formatUploadQueueProgressLine(drafts),
        peakConcurrency,
      })
    }
  }

  emit()

  const runOne = async draft => {
    const started = transitionUploadDraft(draft, UPLOAD_DRAFT_STATUS.READING)
    drafts = replaceDraft(drafts, started)
    active += 1
    peakConcurrency = Math.max(peakConcurrency, active)
    emit()

    let result
    try {
      result = await processItem(started, { isCancelled })
    } catch (error) {
      result = {
        ok: false,
        error: error?.message || 'OCR failed',
      }
    }

    active -= 1

    // If cancelled mid-flight, still record outcome so the draft is not stuck READING.
    const current =
      drafts.find(d => String(d.id) === String(started.id)) || started
    if (normaliseIsReading(current)) {
      if (result?.ok) {
        drafts = replaceDraft(
          drafts,
          transitionUploadDraft(current, UPLOAD_DRAFT_STATUS.READY, {
            ...(result.patch || {}),
            error: null,
          }),
        )
      } else {
        drafts = replaceDraft(
          drafts,
          transitionUploadDraft(current, UPLOAD_DRAFT_STATUS.FAILED, {
            error: result?.error || 'OCR failed',
            ...(result.patch || {}),
          }),
        )
      }
      emit()
    }
  }

  const pump = async () => {
    let keepGoing = true
    while (keepGoing) {
      if (isCancelled()) {
        keepGoing = false
        break
      }

      const nextBatch = selectNextOcrDrafts(drafts, concurrency)
      if (!nextBatch.length) {
        if (inFlight.size === 0) {
          keepGoing = false
          break
        }
        await Promise.race(inFlight)
        continue
      }

      for (const draft of nextBatch) {
        if (isCancelled()) {
          keepGoing = false
          break
        }
        const job = runOne(draft).finally(() => {
          inFlight.delete(job)
        })
        inFlight.add(job)
      }

      if (inFlight.size > 0) {
        await Promise.race(inFlight)
      }
    }

    if (inFlight.size > 0) {
      await Promise.allSettled([...inFlight])
    }
  }

  await pump()

  return {
    drafts,
    peakConcurrency,
    cancelled: Boolean(isCancelled()),
    progressLine: formatUploadQueueProgressLine(drafts),
  }
}

function normaliseIsReading(draft) {
  return String(draft?.status || '') === UPLOAD_DRAFT_STATUS.READING
}

function createdInvoiceFilename(invoice) {
  return String(invoice?.originalFilename || invoice?.filename || '').trim()
}

/**
 * Pair upload items to created invoices.
 * Matches by original filename first (stable even if the upload response is
 * shuffled). Falls back to pick order only when filenames are missing — never
 * steal a sibling's named invoice.
 *
 * @param {object[]} items
 * @param {object[]} newInvoices
 * @param {(item: object) => string} getFilename
 * @returns {(object|null)[]}
 */
export function pairCreatedInvoicesToUploadItems(
  items = [],
  newInvoices = [],
  getFilename = item => item?.filename || item?.name || '',
) {
  const list = Array.isArray(items) ? items : []
  const remaining = (Array.isArray(newInvoices) ? newInvoices : []).filter(
    Boolean,
  )
  const invoicesNamed = remaining.every(inv => createdInvoiceFilename(inv))
  const itemsNamed = list.every(item =>
    Boolean(String(getFilename(item) || '').trim()),
  )
  const allowOrderFallback = !(invoicesNamed && itemsNamed)

  return list.map(item => {
    const filename = String(getFilename(item) || '').trim()
    if (filename) {
      const byName = remaining.findIndex(
        inv => createdInvoiceFilename(inv) === filename,
      )
      if (byName >= 0) {
        return remaining.splice(byName, 1)[0]
      }
    }
    if (allowOrderFallback && remaining.length) {
      return remaining.shift()
    }
    return null
  })
}

/** Pause between OCR retries so a starved sibling can finish. */
export const DEFAULT_OCR_RETRY_DELAYS_MS = [2000, 5000]

/** Per-invoice OCR HTTP timeout. Dense PDFs + header retry can exceed 30s. */
export const INVOICE_OCR_REQUEST_TIMEOUT_MS = 120000

/**
 * Fail-open OCR (HTTP 200, success: false) is retryable unless the service
 * is not configured. Leaves genuine unreadable scans as failed after retries.
 */
export function isRetryableInvoiceOcrResult(ocrData = {}) {
  if (ocrData?.success !== false) return false
  const warnings = Array.isArray(ocrData.warnings) ? ocrData.warnings : []
  if (warnings.includes('ocr_not_configured')) return false
  return true
}

export async function requestOcrWithRetries({
  request,
  isCancelled = () => false,
  delaysMs = DEFAULT_OCR_RETRY_DELAYS_MS,
} = {}) {
  if (typeof request !== 'function') {
    throw new Error('requestOcrWithRetries requires request')
  }
  const delays = Array.isArray(delaysMs) ? delaysMs : []
  let lastError
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    if (isCancelled()) {
      if (lastError) throw lastError
      throw new Error('Invoice OCR cancelled')
    }
    try {
      return await request()
    } catch (error) {
      lastError = error
      if (attempt >= delays.length || isCancelled()) break
      await new Promise(resolve => setTimeout(resolve, delays[attempt]))
    }
  }
  throw lastError
}

/**
 * Attach invoice ids from the multi-file upload response onto drafts (pick order).
 * Matches by originalFilename when unique so scrambled response order cannot
 * attach the wrong invoiceId (and OCR) to a local preview.
 */
export function attachInvoiceIdsToUploadDrafts(drafts = [], newInvoices = []) {
  const paired = pairCreatedInvoicesToUploadItems(
    drafts,
    newInvoices,
    draft => draft?.filename,
  )
  return (drafts || []).map((draft, index) => {
    const created = paired[index]
    const invoiceId = created?._id != null ? String(created._id) : null
    return {
      ...draft,
      invoiceId,
      ...(invoiceId
        ? {}
        : {
            // Leave queued; pool will fail cleanly when OCR starts without id
          }),
    }
  })
}

/**
 * Mark drafts that have no invoice id as failed before the pool runs.
 * Uses queued → reading → failed (only legal path).
 */
export function markMissingInvoiceIdDraftsFailed(drafts = []) {
  return (drafts || []).map(draft => {
    if (draft?.invoiceId) return draft
    if (String(draft?.status) !== UPLOAD_DRAFT_STATUS.QUEUED) return draft
    const reading = transitionUploadDraft(draft, UPLOAD_DRAFT_STATUS.READING)
    return transitionUploadDraft(reading, UPLOAD_DRAFT_STATUS.FAILED, {
      error: 'Upload did not return an invoice id',
    })
  })
}

/**
 * Closing Review later must keep reading remaining files in the pick.
 * Cancelling the queue left the second invoice Unprocessed.
 */
export const REVIEW_LATER_CANCELS_REMAINING_OCR = false

/**
 * After OCR, persist extracted fields + matching without waiting for Confirm.
 */
export function shouldAutoEnrichAfterOcr(file = {}) {
  if (!file?.invoiceId) return false
  if (String(file.ocrStatus || '') === 'failed') return false
  return Boolean(
    String(file.supplierTitle || '').trim() ||
      String(file.invoiceNumber || '').trim() ||
      String(file.purchaseOrderNumber || '').trim() ||
      String(file.invoiceTotalInput || '').trim(),
  )
}
